import { PropsWithChildren, useEffect, useRef, useState } from 'react'
import { useGSAP } from '@gsap/react'
import { Slot } from '@radix-ui/react-slot'
import ScrollTrigger from 'gsap/dist/ScrollTrigger'

import { useLenis } from '@/contexts/lenis'
import { useLoaderStore } from '@/store/loader'

import { Animation } from '@/types'

export type MotionProps = PropsWithChildren<{
  // eslint-disable-next-line no-unused-vars
  animation?: (props: Animation<HTMLElement>) => (() => void) | void // gsap.core.Timeline
  preventOnTouch?: boolean
  revertOnUpdate?: boolean
}>

const Motion = ({
  animation,
  preventOnTouch = true,
  revertOnUpdate = true,
  ...props
}: MotionProps) => {
  const scope = useRef<HTMLDivElement>(null!)
  const { lenis } = useLenis()
  const [fontsLoaded, setFontsLoaded] = useState(false)
  const loaded = useLoaderStore((state) => state.pageLoaded)
  const isPreloaderPlayed = useLoaderStore((state) => state.isPreloaderPlayed)

  // Verifica se i font sono stati caricati
  useEffect(() => {
    if (typeof window === 'undefined') {
      // In SSR, assumiamo che i font siano pronti
      setFontsLoaded(true)
      return
    }

    // Funzione per verificare lo stato dei font
    const checkFontsLoaded = () => {
      if (document.fonts && document.fonts.status === 'loaded') {
        setFontsLoaded(true)
        return true
      }
      return false
    }

    // Controlla subito se i font sono già caricati
    if (checkFontsLoaded()) return

    // Altrimenti attendi che siano caricati
    if (document.fonts) {
      document.fonts.ready.then(() => {
        // Aggiungiamo un breve ritardo per sicurezza
        setTimeout(() => setFontsLoaded(true), 100)
      })
    } else {
      // Fallback per browser che non supportano l'API fonts
      window.addEventListener('load', () => {
        setTimeout(() => setFontsLoaded(true), 500)
      })

      // Se siamo già dopo l'evento load, impostiamo comunque i font come caricati
      if (document.readyState === 'complete') {
        setTimeout(() => setFontsLoaded(true), 500)
      }
    }
  }, [])

  useGSAP(
    (context) => {
      const touch = ScrollTrigger.isTouch > 0
      const prevent = touch && preventOnTouch

      if (!lenis || !fontsLoaded || prevent || !loaded) return

      // Assicuriamo che SplitText non venga utilizzato prima che i font siano caricati
      const revert = animation?.({
        nodeRef: scope.current,
        reverse: false,
        context,
        delay: isPreloaderPlayed ? 0 : 2.5
      })

      return () => {
        revert?.()
      }
    },
    {
      dependencies: [lenis, fontsLoaded, loaded],
      scope,
      revertOnUpdate
    }
  )

  return <Slot ref={scope} {...props} />
}

export default Motion
